// The "General Process" part of a line source's Source tab: which process,
// how each of its criteria is filled from THIS record (an expression over the
// header — `{header.PO_REF}`, `{document}`, a literal), whether that value is
// PROTECTED (shown read-only on the criteria step and never taken from the
// browser), and whether the user sees the criteria step at all or lands
// straight on the lines. The engine reads the same keys
// (`line_entry_process.param_specs`).

import { useEffect, useMemo, useState } from 'react'
import { Trash2, Lock } from 'lucide-react'
import type { LineSourceFrom, LineProcessParam } from '@twasta/page-runtime/txn/types'
import {
  fetchProcessCriteria, listProjectProcesses, type ProjectProcessSummary,
} from '@/api/genprocess'
import type { CriterionItem } from '@/pages/designers/genprocess/types'
import { parseApiError } from '@/livepreview/utils'

const label: React.CSSProperties = {
  display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)',
  margin: '10px 0 4px',
}
const input: React.CSSProperties = {
  width: '100%', padding: '5px 8px', fontSize: '0.8625rem', border: '1px solid var(--color-border-strong)',
  borderRadius: 4, background: 'var(--color-input-bg)', color: 'var(--color-text)', boxSizing: 'border-box',
}
const help: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 3 }
const cell: React.CSSProperties = { padding: '4px 6px', borderBottom: '1px solid var(--color-border)', verticalAlign: 'middle' }

const DOCUMENT_TOKEN = '{document}'

function exprOf(p: LineProcessParam | undefined): string {
  if (p == null) return ''
  return typeof p === 'string' ? p : String(p.value ?? '')
}
function isProtected(p: LineProcessParam | undefined): boolean {
  return typeof p === 'object' && p != null && !!p.protect
}

interface Props {
  project: string
  from: LineSourceFrom
  parentNames: string[]
  onChange: (patch: Partial<LineSourceFrom>) => void
}

export default function LineProcessParams({ project, from, parentNames, onChange }: Props) {
  const [processes, setProcesses] = useState<ProjectProcessSummary[]>([])
  const [criteria, setCriteria] = useState<CriterionItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const name = from.process || ''
  const params = useMemo(() => from.params || {}, [from.params])

  useEffect(() => {
    let cancelled = false
    listProjectProcesses(project)
      .then(r => { if (!cancelled) setProcesses(r.processes || []) })
      .catch(err => { if (!cancelled) setError(`Could not list General Processes: ${parseApiError(err).message}`) })
    return () => { cancelled = true }
  }, [project])

  useEffect(() => {
    let cancelled = false
    if (!name) { setCriteria([]); return }
    setLoading(true)
    setError(null)
    fetchProcessCriteria(project, { objName: name })
      .then(r => { if (!cancelled) setCriteria(r.items || []) })
      .catch(err => {
        if (!cancelled) {
          setCriteria([])
          setError(`Could not read the criteria of '${name}': ${parseApiError(err).message}`)
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [project, name])

  // One row per criterion (case-insensitive), then any mapped name the
  // process no longer has — flagged so a stale mapping is visible.
  const keyOf = (n: string) => Object.keys(params).find(k => k.toLowerCase() === n.toLowerCase())
  const known = new Set(criteria.map(c => c.name.toLowerCase()))
  const stale = Object.keys(params).filter(k => !known.has(k.toLowerCase()))

  const setParam = (crit: string, expr: string, protect: boolean) => {
    const next: Record<string, LineProcessParam> = {}
    for (const [k, v] of Object.entries(params)) if (k.toLowerCase() !== crit.toLowerCase()) next[k] = v
    if (expr.trim()) next[keyOf(crit) || crit] = protect ? { value: expr, protect: true } : expr
    onChange({ params: next })
  }
  const remove = (k: string) => {
    const next = { ...params }
    delete next[k]
    onChange({ params: next })
  }

  const current = processes.find(p => p.obj_name === name)
  const step = from.criteria === 'skip' ? 'skip' : 'ask'
  const anyOpen = criteria.some(c => !isProtected(params[keyOf(c.name) || '']))

  return (
    <div>
      <label style={label}>General Process</label>
      <select style={input} value={name} onChange={e => onChange({ process: e.target.value || undefined })}>
        <option value="">— choose a process —</option>
        {name && !current && <option value={name}>{name} (not found)</option>}
        {processes.map(p => (
          <option key={p.obj_name} value={p.obj_name}>{p.name || p.obj_name} ({p.obj_name})</option>
        ))}
      </select>
      {current && current.persist_mode !== 'none' && (
        <div style={help}>
          This process saves to {current.target || 'its target'} when run on its own. Called from these lines it only
          returns its rows — nothing is written.
        </div>
      )}

      <label style={label}>Criteria — filled from this record</label>
      {loading && <div style={help}>Reading the process criteria…</div>}
      {error && <div style={{ ...help, color: 'var(--color-error)' }}>{error}</div>}
      {!loading && name && criteria.length === 0 && !error && (
        <div style={help}>This process has no criteria prompts.</div>
      )}
      {criteria.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8425rem' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>
              <th style={cell}>Criterion</th>
              <th style={cell}>Value (expression over the header)</th>
              <th style={{ ...cell, width: 90 }}>Protect</th>
            </tr>
          </thead>
          <tbody>
            {criteria.map(c => {
              const k = keyOf(c.name)
              const p = k ? params[k] : undefined
              const expr = exprOf(p)
              const prot = isProtected(p)
              const dflt = c.value != null && c.value !== '' ? String(c.value)
                : c.default_helper ? `@${c.default_helper}` : ''
              return (
                <tr key={c.name}>
                  <td style={cell}>
                    <div style={{ fontWeight: 600 }}>{c.label || c.name}</div>
                    <div style={help}>{c.name} · {c.data_type}{c.multi !== 'single' ? ` · ${c.multi}` : ''}</div>
                  </td>
                  <td style={cell}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input style={{ ...input, flex: 1 }} value={expr}
                        placeholder={dflt ? `process default: ${dflt}` : 'not set — the user enters it'}
                        onChange={e => setParam(c.name, e.target.value, prot)}
                        aria-label={`Value for ${c.label || c.name}`} />
                      <select style={{ ...input, width: 'auto' }} value=""
                        aria-label={`Fill ${c.label || c.name} from`}
                        onChange={e => { if (e.target.value) setParam(c.name, e.target.value, prot) }}>
                        <option value="">from…</option>
                        <option value={DOCUMENT_TOKEN}>each chosen document</option>
                        {parentNames.map(n => <option key={n} value={`{header.${n}}`}>{n}</option>)}
                      </select>
                    </div>
                  </td>
                  <td style={cell}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: expr ? 'pointer' : 'default',
                      opacity: expr ? 1 : 0.5 }}
                      title={expr ? 'The user sees this value but cannot change it' : 'Set a value to protect it'}>
                      <input type="checkbox" checked={prot} disabled={!expr}
                        onChange={e => setParam(c.name, expr, e.target.checked)} />
                      <Lock size={11} />
                    </label>
                  </td>
                </tr>
              )
            })}
            {stale.map(k => (
              <tr key={`stale-${k}`}>
                <td style={cell}>
                  <div style={{ fontWeight: 600 }}>{k}</div>
                  <div style={{ ...help, color: 'var(--color-warning)' }}>not a criterion of this process</div>
                </td>
                <td style={cell}>{exprOf(params[k])}</td>
                <td style={cell}>
                  <button type="button" aria-label={`Remove ${k}`} onClick={() => remove(k)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={help}>
        An unmapped criterion keeps the process&apos;s own default. A protected value is shown read-only and is always
        taken from the record — never from the browser.
      </div>

      <label style={label}>When the user opens it</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8625rem' }}>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
          <input type="radio" checked={step === 'ask'} onChange={() => onChange({ criteria: undefined })} />
          Show the criteria first — the user may change values that are not protected
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
          <input type="radio" checked={step === 'skip'} onChange={() => onChange({ criteria: 'skip' })} />
          Go straight to the lines
        </label>
      </div>
      {step === 'ask' && criteria.length > 0 && !anyOpen && (
        <div style={help}>Every criterion is protected, so the user goes straight to the lines anyway.</div>
      )}
    </div>
  )
}
